import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Tooltip, Alert, CircularProgress, Snackbar, Stepper, Step,
  StepLabel, StepContent, Divider,
} from '@mui/material';
import AddIcon          from '@mui/icons-material/Add';
import EditIcon         from '@mui/icons-material/Edit';
import DeleteIcon       from '@mui/icons-material/Delete';
import RefreshIcon      from '@mui/icons-material/Refresh';
import WifiIcon         from '@mui/icons-material/Wifi';
import WifiOffIcon      from '@mui/icons-material/WifiOff';
import ContentCopyIcon  from '@mui/icons-material/ContentCopy';
import CheckCircleIcon  from '@mui/icons-material/CheckCircle';
import SettingsIcon     from '@mui/icons-material/Settings';
import TerminalIcon     from '@mui/icons-material/Terminal';
import MyLocationIcon   from '@mui/icons-material/MyLocation';
import LockIcon         from '@mui/icons-material/Lock';
import LockOpenIcon     from '@mui/icons-material/LockOpen';
import Switch           from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../utils/api';
import { useAuth } from '../utils/AuthContext';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Recenter map when position changes
function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => { map.setView([lat, lng], 14); }, [map, lat, lng]);
  return null;
}

const EMPTY = { name:'', slug:'', domain:'', ssh_host:'', ssh_user:'root', ssh_port:22, app_dir:'/var/www/HOA', frontend_dir:'', notes:'', address:'', sync_locked: false };
const STATUS_COLORS = { healthy:'#16a34a', offline:'#dc2626', degraded:'#f59e0b', unknown:'#6b778c', deploying:'#2f63ad' };
const fmtDate = iso => iso ? new Date(iso).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : '-';

function DbStatusDot({ status, error }) {
  const ok  = status === 'ok';
  const clr = ok ? '#16a34a' : status === 'down' ? '#dc2626' : '#9aa5b4';
  const lbl = ok ? 'Connected' : status === 'down' ? `Down${error ? ': ' + error : ''}` : 'Unknown';
  return (
    <Tooltip title={lbl}>
      <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5 }}>
        <Box sx={{ width:8, height:8, borderRadius:'50%', backgroundColor:clr, flexShrink:0 }} />
        <Typography variant="caption" sx={{ color:clr, fontWeight:700, fontSize:'0.7rem' }}>
          {ok ? 'OK' : status === 'down' ? 'DOWN' : '-'}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function CopyBox({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <Box sx={{ mb: 2 }}>
      {label && <Typography variant="caption" sx={{ color:'#6b778c', fontWeight:600, mb:0.5, display:'block' }}>{label}</Typography>}
      <Box sx={{ display:'flex', alignItems:'center', gap:1, backgroundColor:'#0f1f35', borderRadius:'10px', px:2, py:1.2 }}>
        <Typography sx={{ fontFamily:'monospace', fontSize:'0.78rem', color:'#7dd3fc', flex:1, wordBreak:'break-all', whiteSpace:'pre-wrap' }}>{value}</Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy'}>
          <IconButton size="small" onClick={copy} sx={{ color: copied ? '#4ade80' : '#94a3b8', flexShrink:0 }}>
            {copied ? <CheckCircleIcon sx={{ fontSize:18 }} /> : <ContentCopyIcon sx={{ fontSize:18 }} />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function OnboardingDialog({ open, onClose, tenant, onDone }) {
  const [info, setInfo]           = useState(null);
  const [sshResult, setSshResult] = useState(null);
  const [sshLoading, setSshLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupDone, setSetupDone] = useState(false);

  useEffect(() => {
    if (open) {
      setSshResult(null); setSetupDone(false);
      api.get('/tenants/onboarding-info').then(r => setInfo(r.data)).catch(() => {});
    }
  }, [open]);

  const testSsh = async () => {
    setSshLoading(true); setSshResult(null);
    try {
      const r = await api.post(`/tenants/${tenant.id}/test-ssh`);
      setSshResult(r.data);
    } catch { setSshResult({ success: false, error: 'Request failed' }); }
    finally { setSshLoading(false); }
  };

  const runSetup = async () => {
    setSetupLoading(true);
    try { await api.post(`/tenants/${tenant.id}/run-setup`); setSetupDone(true); }
    catch { }
    finally { setSetupLoading(false); }
  };

  if (!tenant) return null;

  const ufwCmd   = info ? `ufw allow from ${info.adminIp} to any port ${tenant.ssh_port || 22} proto tcp` : '…';
  const authLine = info ? info.pubKey : '…';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx:{ borderRadius:'16px' } }}>
      <DialogTitle sx={{ fontWeight:800, color:'#1e3a5f', pb:0 }}>
        Onboarding - {tenant.name}
        <Typography variant="body2" sx={{ color:'#6b778c', fontWeight:400, mt:0.3 }}>
          Follow these steps to connect the new server to this admin panel.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt:2 }}>
        <Stepper orientation="vertical" nonLinear sx={{ '& .MuiStepLabel-root':{ py:0.5 } }}>

          {/* Step 1 */}
          <Step active expanded>
            <StepLabel StepIconProps={{ sx:{ color:'#2f63ad' } }}>
              <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>Allow SSH from admin server</Typography>
            </StepLabel>
            <StepContent sx={{ borderLeft:'2px solid #e2e8f0', ml:1.3 }}>
              <Typography variant="body2" sx={{ color:'#6b778c', mb:1.5 }}>
                Run this on <strong>{tenant.ssh_host}</strong> to open port {tenant.ssh_port || 22} for this admin server:
              </Typography>
              <CopyBox value={ufwCmd} />
            </StepContent>
          </Step>

          {/* Step 2 */}
          <Step active expanded>
            <StepLabel>
              <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>Authorize the deploy SSH key</Typography>
            </StepLabel>
            <StepContent sx={{ borderLeft:'2px solid #e2e8f0', ml:1.3 }}>
              <Typography variant="body2" sx={{ color:'#6b778c', mb:1.5 }}>
                Run this on <strong>{tenant.ssh_host}</strong> to allow passwordless deploys:
              </Typography>
              <CopyBox label="One-liner (creates ~/.ssh if needed):" value={`mkdir -p ~/.ssh && echo "${authLine}" >> ~/.ssh/authorized_keys && chmod 600 ~/.ssh/authorized_keys`} />
              <Divider sx={{ my:1.5 }} />
              <Typography variant="caption" sx={{ color:'#9aa5b4' }}>Or paste just the public key manually into <code>~/.ssh/authorized_keys</code>:</Typography>
              <CopyBox label="Public key:" value={authLine} />
            </StepContent>
          </Step>

          {/* Step 3 - Test SSH */}
          <Step active expanded>
            <StepLabel>
              <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>Test SSH connection</Typography>
            </StepLabel>
            <StepContent sx={{ borderLeft:'2px solid #e2e8f0', ml:1.3 }}>
              <Typography variant="body2" sx={{ color:'#6b778c', mb:1.5 }}>
                Verify the admin server can connect to <strong>{tenant.ssh_host}</strong>:
              </Typography>
              <Button variant="outlined" startIcon={sshLoading ? <CircularProgress size={16}/> : <TerminalIcon />}
                onClick={testSsh} disabled={sshLoading}
                sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600, borderColor:'rgba(47,99,173,0.4)', color:'#2f63ad', mb:1.5 }}>
                {sshLoading ? 'Testing…' : 'Test SSH Connection'}
              </Button>
              {sshResult && (
                <Alert severity={sshResult.success ? 'success' : 'error'} sx={{ borderRadius:'10px', mb:1 }}>
                  {sshResult.success ? 'Connected successfully!' : `Failed: ${sshResult.error}`}
                </Alert>
              )}
              {sshResult?.output && (
                <Box sx={{ backgroundColor:'#0f1f35', borderRadius:'10px', p:1.5, fontFamily:'monospace', fontSize:'0.75rem', color:'#7dd3fc', whiteSpace:'pre-wrap' }}>
                  {sshResult.output}
                </Box>
              )}
            </StepContent>
          </Step>

          {/* Step 4 - Run setup */}
          <Step active expanded>
            <StepLabel>
              <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>Run remote setup</Typography>
            </StepLabel>
            <StepContent sx={{ borderLeft:'2px solid #e2e8f0', ml:1.3 }}>
              <Typography variant="body2" sx={{ color:'#6b778c', mb:1.5 }}>
                Copies migration files, scripts, and creates a <code>.env</code> template on the server.
                Requires SSH to be working (Step 3).
              </Typography>
              {setupDone
                ? <Alert severity="success" sx={{ borderRadius:'10px' }}>Setup complete! Fill in <code>{tenant.app_dir || '/var/www/HOA'}/.env</code> with DB credentials, then deploy.</Alert>
                : <Button variant="contained" startIcon={setupLoading ? <CircularProgress size={16} color="inherit"/> : <SettingsIcon />}
                    onClick={runSetup} disabled={setupLoading || !sshResult?.success}
                    sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, backgroundColor:'#2f63ad', boxShadow:'none', '&:hover':{ backgroundColor:'#1f4f94', boxShadow:'none' } }}>
                    {setupLoading ? 'Running setup…' : 'Run Setup on Server'}
                  </Button>
              }
              {!sshResult?.success && <Typography variant="caption" sx={{ display:'block', mt:1, color:'#9aa5b4' }}>Complete Step 3 first.</Typography>}
            </StepContent>
          </Step>

          {/* Step 5 */}
          <Step active expanded>
            <StepLabel>
              <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>Fill in .env and deploy</Typography>
            </StepLabel>
            <StepContent sx={{ borderLeft:'2px solid #e2e8f0', ml:1.3 }}>
              <Typography variant="body2" sx={{ color:'#6b778c', mb:1 }}>SSH in and fill DB credentials:</Typography>
              <CopyBox value={`ssh root@${tenant.ssh_host}\nnano ${tenant.app_dir || '/var/www/HOA'}/.env`} />
              <Typography variant="body2" sx={{ color:'#6b778c', mt:1 }}>Then go to <strong>Deployments → New Deploy</strong> to push the app.</Typography>
            </StepContent>
          </Step>

        </Stepper>
      </DialogContent>
      <DialogActions sx={{ px:3, pb:2.5 }}>
        <Button onClick={() => { onClose(); onDone(); }}
          sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, backgroundColor:'#2f63ad', color:'#fff', boxShadow:'none', '&:hover':{ backgroundColor:'#1f4f94', boxShadow:'none' } }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Tenants() {
  const { hasRole } = useAuth();
  const [tenants, setTenants]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [pinging, setPinging]   = useState({});
  const [dialog, setDialog]     = useState({ open: false, mode: 'create', data: EMPTY });
  const [geoState, setGeoState] = useState({ loading: false, result: null, error: '' }); // {lat,lng} or null
  const [saving, setSaving]     = useState(false);
  const [snack, setSnack]       = useState({ open:false, msg:'', sev:'success' });
  const [delConfirm, setDelConfirm]   = useState(null);
  const [onboarding, setOnboarding]   = useState(null); // tenant being onboarded

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/tenants'); setTenants(r.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ping = async (id) => {
    setPinging(p => ({ ...p, [id]: true }));
    try { await api.post(`/tenants/${id}/ping`); await load(); }
    finally { setPinging(p => ({ ...p, [id]: false })); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (dialog.mode === 'create') {
        const r = await api.post('/tenants', dialog.data);
        setDialog(d => ({ ...d, open: false }));
        await load();
        // Open onboarding wizard for newly created tenant
        const created = (await api.get('/tenants')).data.find(t => t.id === r.data.id);
        if (created) setOnboarding(created);
      } else {
        await api.put(`/tenants/${dialog.data.id}`, dialog.data);
        setSnack({ open:true, msg:'Tenant updated', sev:'success' });
        setDialog(d => ({ ...d, open: false }));
        load();
      }
    } catch(err) {
      setSnack({ open:true, msg: err.response?.data?.message || 'Failed to save', sev:'error' });
    } finally { setSaving(false); }
  };

  const deleteTenant = async (id) => {
    try { await api.delete(`/tenants/${id}`); setSnack({ open:true, msg:'Tenant deleted', sev:'info' }); load(); }
    catch { setSnack({ open:true, msg:'Failed to delete', sev:'error' }); }
    setDelConfirm(null);
  };

  const field = (label, key, opts={}) => (
    <Grid item xs={opts.xs||12} sm={opts.sm||6}>
      <TextField fullWidth size="small" label={label} value={dialog.data[key]||''} type={opts.type||'text'}
        helperText={opts.helperText}
        onChange={e => setDialog(d => ({ ...d, data: { ...d.data, [key]: e.target.value } }))}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight:800, color:'#1e3a5f' }}>Tenants</Typography>
          <Typography variant="body2" sx={{ color:'#6b778c' }}>Manage client servers</Typography>
        </Box>
        <Box sx={{ display:'flex', gap:1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load}
            sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600, borderColor:'rgba(47,99,173,0.3)', color:'#2f63ad' }}>Refresh</Button>
          {hasRole('admin') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setGeoState({ loading:false, result:null, error:'' }); setDialog({ open:true, mode:'create', data:EMPTY }); }}
              sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, backgroundColor:'#2f63ad', boxShadow:'none', '&:hover':{ backgroundColor:'#1f4f94', boxShadow:'none' } }}>
              Add Tenant
            </Button>
          )}
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius:'16px', border:'1px solid rgba(47,99,173,0.08)', overflow:'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor:'#f8fafc' }}>
              {['Name','Domain','Status','DB1','DB2','Version','SSH Host','Last Seen','Sync','Actions'].map(h => (
                <TableCell key={h} sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a', py:1.5 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? Array.from({length:3}).map((_,i) => (
              <TableRow key={i}><TableCell colSpan={10} sx={{ py:3, textAlign:'center' }}><CircularProgress size={24} /></TableCell></TableRow>
            )) : tenants.length === 0 ? (
              <TableRow><TableCell colSpan={10} sx={{ textAlign:'center', py:6, color:'#9aa5b4' }}>No tenants yet. Add one to get started.</TableCell></TableRow>
            ) : tenants.map(t => (
              <TableRow key={t.id} hover sx={{ '&:hover':{ backgroundColor:'rgba(47,99,173,0.02)' } }}>
                <TableCell sx={{ py:1, fontWeight:700, color:'#1e3a5f' }}>
                  {t.name}
                  <Typography variant="caption" sx={{ display:'block', color:'#9aa5b4' }}>/{t.slug}</Typography>
                </TableCell>
                <TableCell sx={{ py:1, fontSize:'0.82rem', color:'#445' }}>{t.domain}</TableCell>
                <TableCell sx={{ py:1 }}>
                  <Chip label={t.status || 'unknown'} size="small"
                    sx={{ height:22, fontSize:'0.7rem', fontWeight:700, borderRadius:'6px',
                      backgroundColor:`${STATUS_COLORS[t.status||'unknown']}18`,
                      color: STATUS_COLORS[t.status||'unknown'] }} />
                </TableCell>
                {(() => {
                  let db1 = null, db2 = null;
                  try { const s = JSON.parse(t.db_status || 'null'); db1 = s?.db1; db2 = s?.db2; } catch {}
                  return (<>
                    <TableCell sx={{ py:1 }}><DbStatusDot status={db1?.status} error={db1?.error} /></TableCell>
                    <TableCell sx={{ py:1 }}><DbStatusDot status={db2?.status} error={db2?.error} /></TableCell>
                  </>);
                })()}
                <TableCell sx={{ py:1, fontFamily:'monospace', fontSize:'0.82rem', color:'#445' }}>
                  {t.current_version ? `v${t.current_version}` : '-'}
                </TableCell>
                <TableCell sx={{ py:1, fontFamily:'monospace', fontSize:'0.8rem', color:'#6b778c' }}>{t.ssh_host}</TableCell>
                <TableCell sx={{ py:1, fontSize:'0.78rem', color:'#6b778c' }}>{fmtDate(t.last_seen)}</TableCell>
                <TableCell sx={{ py:1 }}>
                  {t.sync_locked ? (
                    <Tooltip title="Sync locked - click to unlock">
                      <Chip
                        icon={<LockIcon sx={{ fontSize:'14px !important' }} />}
                        label="Locked"
                        size="small"
                        onClick={() => hasRole('admin') && api.put(`/tenants/${t.id}`, { sync_locked: false }).then(load)}
                        sx={{ height:22, fontSize:'0.68rem', fontWeight:700, cursor: hasRole('admin') ? 'pointer' : 'default',
                          backgroundColor:'rgba(239,68,68,0.12)', color:'#ef4444', borderRadius:'6px' }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Sync enabled - click to lock">
                      <Chip
                        icon={<LockOpenIcon sx={{ fontSize:'14px !important' }} />}
                        label="Active"
                        size="small"
                        onClick={() => hasRole('admin') && api.put(`/tenants/${t.id}`, { sync_locked: true }).then(load)}
                        sx={{ height:22, fontSize:'0.68rem', fontWeight:700, cursor: hasRole('admin') ? 'pointer' : 'default',
                          backgroundColor:'rgba(34,197,94,0.12)', color:'#22c55e', borderRadius:'6px' }}
                      />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ py:1 }}>
                  <Box sx={{ display:'flex', gap:0.5 }}>
                    <Tooltip title="Ping health check">
                      <IconButton size="small" onClick={() => ping(t.id)} disabled={pinging[t.id]}>
                        {pinging[t.id] ? <CircularProgress size={16} /> : t.status === 'healthy'
                          ? <WifiIcon sx={{ fontSize:18, color:'#16a34a' }} />
                          : <WifiOffIcon sx={{ fontSize:18, color:'#dc2626' }} />}
                      </IconButton>
                    </Tooltip>
                    {hasRole('admin') && <>
                      <Tooltip title="Onboarding / Setup">
                        <IconButton size="small" onClick={() => setOnboarding(t)}>
                          <SettingsIcon sx={{ fontSize:18, color: t.status === 'offline' ? '#f59e0b' : '#6b778c' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => { setGeoState({ loading:false, result: t.latitude ? { lat:t.latitude, lng:t.longitude } : null, error:'' }); setDialog({ open:true, mode:'edit', data:{...t} }); }}>
                          <EditIcon sx={{ fontSize:18, color:'#2f63ad' }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDelConfirm(t)}>
                          <DeleteIcon sx={{ fontSize:18, color:'#dc2626' }} />
                        </IconButton>
                      </Tooltip>
                    </>}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Onboarding wizard */}
      <OnboardingDialog
        open={!!onboarding}
        tenant={onboarding}
        onClose={() => setOnboarding(null)}
        onDone={load}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog(d=>({...d,open:false}))} maxWidth="md" fullWidth PaperProps={{ sx:{borderRadius:'16px'} }}>
        <DialogTitle sx={{ fontWeight:700, color:'#1e3a5f' }}>{dialog.mode==='create' ? 'Add Tenant' : 'Edit Tenant'}</DialogTitle>
        <DialogContent sx={{ pt:1 }}>
          <Grid container spacing={2} sx={{ mt:0 }}>
            {field('Name *','name',{sm:6})}
            {field('Slug *','slug',{sm:6})}
            {field('Domain *','domain',{sm:12})}
            {field('SSH Host *','ssh_host',{sm:6})}
            {field('SSH User','ssh_user',{sm:3})}
            {field('SSH Port','ssh_port',{sm:3,type:'number'})}
            {field('App Directory (backend)','app_dir',{sm:6})}
            {field('Frontend Dir (nginx root)','frontend_dir',{sm:6,helperText:'e.g. /var/www/heritagespringscommunity - leave blank to auto-derive from domain'})}
            {/* Address with verify */}
            <Grid item xs={12}>
              <Box sx={{ display:'flex', gap:1, alignItems:'flex-start' }}>
                <TextField
                  fullWidth size="small" label="Community Address"
                  value={dialog.data.address || ''}
                  onChange={e => {
                    setDialog(d => ({ ...d, data: { ...d.data, address: e.target.value } }));
                    setGeoState(g => ({ ...g, result: null, error: '' }));
                  }}
                  helperText="Full address to pin on the map - click Verify to confirm location"
                  sx={{ '& .MuiOutlinedInput-root':{ borderRadius:'10px' } }}
                />
                <Button
                  variant="outlined" size="small"
                  startIcon={geoState.loading ? <CircularProgress size={14} /> : <MyLocationIcon />}
                  disabled={geoState.loading || !dialog.data.address}
                  onClick={async () => {
                    setGeoState({ loading:true, result:null, error:'' });
                    try {
                      const r = await api.post('/tenants/geocode', { address: dialog.data.address });
                      setGeoState({ loading:false, result:{ lat:r.data.lat, lng:r.data.lng }, error:'' });
                    } catch(e) {
                      setGeoState({ loading:false, result:null, error: e.response?.data?.message || 'Not found' });
                    }
                  }}
                  sx={{ mt:'1px', minWidth:100, borderRadius:'10px', textTransform:'none', fontWeight:600, whiteSpace:'nowrap',
                    borderColor:'rgba(47,99,173,0.3)', color:'#2f63ad' }}
                >
                  Verify
                </Button>
              </Box>
              {geoState.error && (
                <Alert severity="warning" sx={{ mt:1, py:0.5, borderRadius:'10px' }}>{geoState.error}</Alert>
              )}
              {geoState.result && (
                <Box sx={{ mt:1.5, borderRadius:'12px', overflow:'hidden', border:'1px solid rgba(47,99,173,0.15)', height:200 }}>
                  <MapContainer center={[geoState.result.lat, geoState.result.lng]} zoom={14}
                    style={{ height:'100%', width:'100%' }} scrollWheelZoom={false} zoomControl={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; OpenStreetMap contributors' />
                    <Marker position={[geoState.result.lat, geoState.result.lng]} />
                    <Recenter lat={geoState.result.lat} lng={geoState.result.lng} />
                  </MapContainer>
                  <Box sx={{ px:1.5, py:0.75, backgroundColor:'rgba(22,163,74,0.06)', display:'flex', alignItems:'center', gap:0.75 }}>
                    <CheckCircleIcon sx={{ fontSize:15, color:'#16a34a' }} />
                    <Typography variant="caption" color="#16a34a" fontWeight={600}>
                      Location verified · {geoState.result.lat.toFixed(5)}, {geoState.result.lng.toFixed(5)}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>
            {field('Notes','notes',{sm:12})}

            {/* Sync lock */}
            <Grid item xs={12}>
              <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:1.5, py:1.25,
                borderRadius:'10px', border:'1px solid', borderColor: dialog.data.sync_locked ? 'rgba(239,68,68,0.3)' : 'divider',
                backgroundColor: dialog.data.sync_locked ? 'rgba(239,68,68,0.05)' : 'transparent',
                transition:'all 0.2s',
              }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                  {dialog.data.sync_locked
                    ? <LockIcon sx={{ fontSize:18, color:'#ef4444' }} />
                    : <LockOpenIcon sx={{ fontSize:18, color:'#22c55e' }} />}
                  <Box>
                    <Typography variant="body2" fontWeight={700} color={dialog.data.sync_locked ? '#ef4444' : 'text.primary'}>
                      {dialog.data.sync_locked ? 'Sync Locked' : 'Sync Enabled'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dialog.data.sync_locked
                        ? 'This tenant is protected - sync and deploy are blocked.'
                        : 'This tenant can receive sync and deploy operations.'}
                    </Typography>
                  </Box>
                </Box>
                <Switch
                  checked={!!dialog.data.sync_locked}
                  onChange={e => setDialog(d => ({ ...d, data: { ...d.data, sync_locked: e.target.checked } }))}
                  sx={{ '& .MuiSwitch-thumb': { backgroundColor: dialog.data.sync_locked ? '#ef4444' : '#22c55e' } }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
          <Button onClick={() => setDialog(d=>({...d,open:false}))} sx={{ borderRadius:'10px', textTransform:'none', fontWeight:600, color:'#6b778c' }}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}
            sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, backgroundColor:'#2f63ad', boxShadow:'none', '&:hover':{ backgroundColor:'#1f4f94', boxShadow:'none' } }}>
            {saving ? 'Saving…' : dialog.mode==='create' ? 'Create Tenant' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!delConfirm} onClose={() => setDelConfirm(null)} PaperProps={{ sx:{borderRadius:'16px'} }}>
        <DialogTitle sx={{ fontWeight:700, color:'#b91c1c' }}>Delete Tenant</DialogTitle>
        <DialogContent><Typography>Are you sure you want to delete <strong>{delConfirm?.name}</strong>? This will remove all deploy history.</Typography></DialogContent>
        <DialogActions sx={{ px:3, pb:2 }}>
          <Button onClick={() => setDelConfirm(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => deleteTenant(delConfirm.id)}
            sx={{ backgroundColor:'#dc2626','&:hover':{backgroundColor:'#b91c1c'}, borderRadius:'10px', textTransform:'none', fontWeight:700, boxShadow:'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s=>({...s,open:false}))} sx={{ borderRadius:'12px' }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
